import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, doc, getDocs, query, where,
  updateDoc, onSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "./App";

// ─── Status Indicator ─────────────────────────────────────────────────────────

function StatusDot({ ok }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: ok ? "#00C853" : "#E53935",
      marginRight: 6, flexShrink: 0,
      boxShadow: ok ? "0 0 6px rgba(0,200,83,0.6)" : "0 0 6px rgba(229,57,53,0.6)",
    }} />
  );
}

function StatBox({ label, value, sub, accent }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "12px 16px",
      border: "1px solid rgba(255,255,255,0.08)",
    }}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: accent || "white", letterSpacing: 1 }}>
        {value ?? "—"}
      </div>
      {sub && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Override Panel ───────────────────────────────────────────────────────────

function OverridePanel({ contestId, week }) {
  const [games, setGames] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  const addLog = (msg) => setLog((p) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...p]);

  useEffect(() => {
    if (!contestId || !week) return;
    const q = query(
      collection(db, "ue_games"),
      where("contestId", "==", contestId),
      where("week", "==", week)
    );
    getDocs(q).then((snap) => {
      setGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.kickoff?.toDate ? a.kickoff.toDate() : new Date(a.kickoff || 0);
        const tb = b.kickoff?.toDate ? b.kickoff.toDate() : new Date(b.kickoff || 0);
        return ta - tb;
      }));
    });
  }, [contestId, week]);

  const forceScore = async (game) => {
    setLoading(true);
    addLog(`Force-scoring ${game.awayTeam} @ ${game.homeTeam}...`);
    try {
      await updateDoc(doc(db, "ue_games", game.id), { status: "final" });
      addLog(`✅ Marked as final. Scoring will run on next pollScores cycle (≤5 min).`);
    } catch (e) {
      addLog(`ERROR: ${e.message}`);
    }
    setLoading(false);
  };

  const unlockClosingLine = async (game) => {
    setLoading(true);
    addLog(`Unlocking closing line for ${game.awayTeam} @ ${game.homeTeam}...`);
    try {
      await updateDoc(doc(db, "ue_games", game.id), {
        closingLineLocked: false,
        closingSpread: null,
        closingUnderdogTeam: null,
      });
      addLog(`✅ Closing line unlocked — will be re-fetched on next sync.`);
    } catch (e) {
      addLog(`ERROR: ${e.message}`);
    }
    setLoading(false);
  };

  const setManualScore = async (game, homeScore, awayScore) => {
    if (homeScore === null || awayScore === null) return;
    setLoading(true);
    addLog(`Setting manual score: ${game.awayTeam} ${awayScore} @ ${game.homeTeam} ${homeScore}`);
    try {
      await updateDoc(doc(db, "ue_games", game.id), {
        homeScore: parseInt(homeScore),
        awayScore: parseInt(awayScore),
        status: "final",
        manuallyScored: true,
      });
      addLog(`✅ Score set. Picks will be scored on next pollScores cycle.`);
    } catch (e) {
      addLog(`ERROR: ${e.message}`);
    }
    setLoading(false);
  };

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 12 }}>
        MANUAL OVERRIDES — WEEK {week}
      </div>

      {games.length === 0 && (
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No games loaded for this week/contest.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {games.map((game) => (
          <OverrideGameRow
            key={game.id}
            game={game}
            onForceScore={() => forceScore(game)}
            onUnlockLine={() => unlockClosingLine(game)}
            onManualScore={(h, a) => setManualScore(game, h, a)}
            disabled={loading}
          />
        ))}
      </div>

      {log.length > 0 && (
        <div style={{
          background: "rgba(0,0,0,0.4)", borderRadius: 8, padding: 12,
          fontFamily: "monospace", fontSize: 11, maxHeight: 150, overflowY: "auto",
        }}>
          {log.map((l, i) => (
            <div key={i} style={{
              color: l.includes("ERROR") ? "#ff6b6b" : l.includes("✅") ? "#00C853" : "rgba(255,255,255,0.4)",
              marginBottom: 3,
            }}>
              {l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverrideGameRow({ game, onForceScore, onUnlockLine, onManualScore, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const [manualHome, setManualHome] = useState("");
  const [manualAway, setManualAway] = useState("");

  return (
    <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, overflow: "hidden" }}>
      <div
        onClick={() => setExpanded((e) => !e)}
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 14px", cursor: "pointer",
        }}
      >
        <div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{game.awayTeam} @ {game.homeTeam}</span>
          {game.isMNF && <span style={{ marginLeft: 8, fontSize: 10, color: "#FFD700", fontWeight: 700 }}>MNF</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {game.closingLineLocked && <span style={{ fontSize: 10, color: "#FF6B35", fontWeight: 700 }}>LINE LOCKED</span>}
          <span className={`badge badge-${game.status === "final" ? "green" : game.status === "in_progress" ? "yellow" : "gray"}`}>
            {game.status || "scheduled"}
          </span>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 14px 14px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 10, marginTop: 10 }}>
            Dog: <strong style={{ color: "#FF6B35" }}>{game.closingUnderdogTeam || game.underdogTeam || "none"}</strong>
            {" "}+{game.closingSpread ?? game.spread ?? "—"}
            {game.homeScore !== null && (
              <span style={{ marginLeft: 12 }}>Score: {game.awayScore}–{game.homeScore}</span>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {game.status !== "final" && (
              <button onClick={onForceScore} disabled={disabled} className="btn btn-ghost btn-sm">
                Force Final
              </button>
            )}
            {game.closingLineLocked && (
              <button onClick={onUnlockLine} disabled={disabled} className="btn btn-ghost btn-sm">
                Unlock Closing Line
              </button>
            )}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                value={manualAway}
                onChange={(e) => setManualAway(e.target.value)}
                placeholder="Away"
                style={{ width: 50, padding: "4px 8px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", fontSize: 13, textAlign: "center" }}
              />
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>–</span>
              <input
                value={manualHome}
                onChange={(e) => setManualHome(e.target.value)}
                placeholder="Home"
                style={{ width: 50, padding: "4px 8px", borderRadius: 6, background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", fontSize: 13, textAlign: "center" }}
              />
              <button
                onClick={() => onManualScore(manualHome, manualAway)}
                disabled={disabled || !manualHome || !manualAway}
                className="btn btn-sm"
                style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}
              >
                Set Score
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Admin Panel ─────────────────────────────────────────────────────────

export default function UEAdminPanel() {
  const { } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState("");
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [rescoring, setRescoring] = useState(false);
  const [rescoreWeekNum, setRescoreWeekNum] = useState(1);
  const [rescoreStatus, setRescoreStatus] = useState("");

  const rescoreWeek = async () => {
    if (!selectedContest) { setRescoreStatus("✗ Select a contest in Overrides tab first."); return; }
    setRescoring(true); setRescoreStatus("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "rescoreWeek")({
        contestId: selectedContest,
        week: rescoreWeekNum,
      });
      setRescoreStatus("✓ " + result.data.message);
    } catch (err) {
      setRescoreStatus("✗ " + err.message);
    }
    setRescoring(false);
  };

  const [assigning, setAssigning] = useState(false);
  const [assignWeekNum, setAssignWeekNum] = useState(1);
  const [assignStatus, setAssignStatus] = useState("");

  const runAutoAssignNow = async () => {
    if (!selectedContest) { setAssignStatus("✗ Select a contest in Overrides tab first."); return; }
    setAssigning(true); setAssignStatus("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "runAutoAssignNow")({
        contestId: selectedContest,
        week: assignWeekNum,
      });
      setAssignStatus((result.data.success ? "✓ " : "✗ ") + result.data.message);
    } catch (err) {
      setAssignStatus("✗ " + err.message);
    }
    setAssigning(false);
  };

  const exportEmails = async (contestId) => {
    const cid = contestId || selectedContest;
    if (!cid) { setExportStatus("✗ Select a contest in Overrides tab first."); return; }
    setExporting(true); setExportStatus("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "getUEContestEmails")({ contestId: cid });
      const { entries, playedWeeks } = result.data;

      // Build header with week columns
      const weekHeaders = playedWeeks.flatMap((w) =>
        [`Wk${w} Pick`, `Wk${w} Result`, `Wk${w} Pts`]
      );
      const header = ["Rank", "Entry Name", "Display Name", "Email", "Total Points", ...weekHeaders].join(",");

      const lines = entries.map((r) => {
        const weekCols = playedWeeks.flatMap((w) => [
          `"${r[`week${w}_pick`] || "—"}"`,
          `"${r[`week${w}_result`] || "—"}"`,
          r[`week${w}_pts`] ?? 0,
        ]);
        return [r.rank, `"${r.entryName}"`, `"${r.ownerName}"`, `"${r.email}"`, r.totalPoints, ...weekCols].join(",");
      });

      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `underdog-edge-export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setExportStatus(`✓ Exported ${entries.length} entries, ${playedWeeks.length} week(s)`);
    } catch (err) {
      setExportStatus("✗ Export failed: " + err.message);
    }
    setExporting(false);
  };
  const [tab, setTab] = useState("status");

  useEffect(() => {
    if (!open) return;

    // Live status listener
    const unsub = onSnapshot(doc(db, "ue_system", "status"), (snap) => {
      if (snap.exists()) setStatus(snap.data());
    });

    // Load contests
    getDocs(collection(db, "ue_contests")).then((snap) => {
      setContests(snap.docs.map((d) => ({ id: d.id, name: d.data().name })));
    });

    return () => unsub();
  }, [open]);

  const formatTime = (ts) => {
    if (!ts) return "Never";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
  };

  const syncStatus = status?.oddsSync;
  const scoreStatus = status?.scoreSync;

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState("");
  const [histDate, setHistDate] = useState("2026-08-14T20:00:00Z");
  const [histWeek, setHistWeek] = useState(1);
  const [histResult, setHistResult] = useState("");
  const [histSyncing, setHistSyncing] = useState(false);


  const syncHistorical = async () => {
    if (!selectedContest) { setHistResult("✗ Select a contest first in the Overrides tab."); return; }
    setHistSyncing(true); setHistResult("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "syncHistoricalWeek")({
        date: histDate,
        week: histWeek,
        contestId: selectedContest,
      });
      setHistResult("✓ " + result.data.message);
    } catch (err) {
      setHistResult("✗ " + err.message);
    }
    setHistSyncing(false);
  };

  const syncNow = async () => {
    setSyncing(true); setSyncResult("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "syncOddsNow")();
      setSyncResult("✓ " + result.data.message);
    } catch (err) {
      setSyncResult("✗ Sync failed: " + err.message);
    }
    setSyncing(false);
  };

  if (!open) {
    return (
      <div style={{ marginTop: 32, textAlign: "center" }}>
        <button onClick={() => setOpen(true)} className="btn btn-ghost btn-sm">
          🔧 Admin
        </button>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 32, border: "1px solid rgba(255,107,53,0.25)", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FF6B35", letterSpacing: 2 }}>
          🔧 SYSTEM STATUS
        </div>
        <button onClick={() => setOpen(false)} className="btn btn-ghost btn-sm">Close</button>
      </div>

      {/* Tabs */}
      <div className="nav-tabs" style={{ marginBottom: 20 }}>
        {[
          { id: "status", label: "📊 Status" },
          { id: "overrides", label: "⚙️ Overrides" },
        ].map((t) => (
          <button
            key={t.id}
            className={"nav-tab" + (tab === t.id ? " active" : "")}
            onClick={() => setTab(t.id)}
            style={tab === t.id ? { background: "#FF6B35", color: "white" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Status Tab ── */}
      {tab === "status" && (
        <div>
          {/* Sync status cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div className="card" style={{ padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 8 }}>
                ODDS SYNC (every 2h)
              </div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                <StatusDot ok={syncStatus?.success} />
                <span style={{ fontSize: 13, color: syncStatus?.success ? "#00C853" : "#E53935" }}>
                  {syncStatus?.success ? "OK" : syncStatus?.error || "No data yet"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
                Last: {formatTime(syncStatus?.updatedAt)}
              </div>
              {syncStatus?.quotaRemaining !== undefined && (
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>
                  API quota remaining: {syncStatus.quotaRemaining}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 8 }}>
                SCORE POLL (every 5m)
              </div>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                <StatusDot ok={scoreStatus?.success} />
                <span style={{ fontSize: 13, color: scoreStatus?.success ? "#00C853" : "#E53935" }}>
                  {scoreStatus?.window || (scoreStatus?.success ? "OK" : "No data yet")}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
                Last: {formatTime(scoreStatus?.updatedAt)}
              </div>
            </div>
          </div>

          {/* Last sync stats */}
          {syncStatus && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 10, marginBottom: 20 }}>
              <StatBox label="GAMES IMPORTED" value={syncStatus.imported} accent="#FF6B35" />
              <StatBox label="GAMES UPDATED" value={syncStatus.updated} />
              <StatBox label="DOG FLIPS" value={syncStatus.flipsDetected} accent={syncStatus.flipsDetected > 0 ? "#E53935" : undefined} />
              <StatBox label="PICKS FLAGGED" value={syncStatus.picksFlagged} accent={syncStatus.picksFlagged > 0 ? "#FFD700" : undefined} />
            </div>
          )}

          {/* Last score poll stats */}
          {scoreStatus && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 10 }}>
              <StatBox label="GAMES FINAL" value={scoreStatus.finalGames} accent="#00C853" />
              <StatBox label="IN PROGRESS" value={scoreStatus.inProgressGames} />
              <StatBox label="LINES LOCKED" value={scoreStatus.closingLinesLocked} accent="#FF6B35" />
              <StatBox label="PICKS SCORED" value={scoreStatus.pickScored} accent="#FFD700" />
            </div>
          )}

          {/* Manual sync button */}
          <div className="card" style={{ marginTop: 20, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 10 }}>MANUAL SYNC</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
              <button onClick={syncNow} disabled={syncing} className="btn btn-sm" style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}>
                {syncing ? "Syncing..." : "⚡ Sync Odds Now"}
              </button>
              {syncResult && (
                <span style={{ fontSize: 13, color: syncResult.startsWith("✓") ? "#00C853" : "#E53935" }}>
                  {syncResult}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", lineHeight: 1.6 }}>
              Forces an immediate odds sync outside the 2-hour schedule. Useful for verifying API connection
              or picking up lines as soon as they are posted.
            </div>
          </div>

          {/* Historical sync */}
          <div className="card" style={{ marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 10 }}>HISTORICAL SYNC (TESTING)</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 4 }}>DATE (ISO)</div>
                <input
                  value={histDate}
                  onChange={(e) => setHistDate(e.target.value)}
                  className="input"
                  style={{ width: 220, fontSize: 12, padding: "8px 10px" }}
                  placeholder="2026-08-14T20:00:00Z"
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 4 }}>WEEK</div>
                <select
                  value={histWeek}
                  onChange={(e) => setHistWeek(Number(e.target.value))}
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "8px 10px", fontSize: 13 }}
                >
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                    <option key={w} value={w}>Week {w}</option>
                  ))}
                </select>
              </div>
              <button onClick={syncHistorical} disabled={histSyncing} className="btn btn-sm" style={{ background: "#FFD700", color: "#0a1628", fontWeight: 700 }}>
                {histSyncing ? "Syncing..." : "📅 Sync Historical"}
              </button>
            </div>
            {histResult && (
              <div style={{ fontSize: 13, color: histResult.startsWith("✓") ? "#00C853" : "#E53935", marginBottom: 8 }}>
                {histResult}
              </div>
            )}
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", lineHeight: 1.5 }}>
              Pulls odds from a specific past date. Select your test contest in Overrides tab first.
              For preseason Week 1, use date: 2026-08-14T20:00:00Z · Week: 1
            </div>
          </div>

          {/* Rescore week */}
          <div className="card" style={{ marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 10 }}>RESCORE WEEK</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 10 }}>
              Recalculates all pick scores and entry totals for a given week. Safe to run multiple times.
              Select a contest in Overrides tab first.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
              <select
                value={rescoreWeekNum}
                onChange={(e) => setRescoreWeekNum(Number(e.target.value))}
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "6px 10px", fontSize: 13 }}
              >
                {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </select>
              <button onClick={rescoreWeek} disabled={rescoring} className="btn btn-sm"
                style={{ background: "#E53935", color: "white", fontWeight: 700 }}>
                {rescoring ? "Rescoring..." : "🔄 Rescore Week"}
              </button>
              {rescoreStatus && (
                <span style={{ fontSize: 13, color: rescoreStatus.startsWith("✓") ? "#00C853" : "#E53935" }}>
                  {rescoreStatus}
                </span>
              )}
            </div>
          </div>

          {/* Run auto-assign */}
          <div className="card" style={{ marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 10 }}>RUN AUTO-ASSIGN</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 10 }}>
              Gives the last game's (MNF) underdog to every entry with no pick or a flipped pick for that week.
              Runs automatically at MNF kickoff — this is the backup. Safe to run multiple times; never touches real picks.
              Won't run before the week's last game kicks off. Select a contest in Overrides tab first.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
              <select
                value={assignWeekNum}
                onChange={(e) => setAssignWeekNum(Number(e.target.value))}
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "6px 10px", fontSize: 13 }}
              >
                {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </select>
              <button onClick={runAutoAssignNow} disabled={assigning} className="btn btn-sm"
                style={{ background: "#FF6B35", color: "white", fontWeight: 700 }}>
                {assigning ? "Assigning..." : "🎯 Run Auto-Assign"}
              </button>
            </div>
            {assignStatus && (
              <div style={{ fontSize: 13, color: assignStatus.startsWith("✓") ? "#00C853" : "#E53935" }}>
                {assignStatus}
              </div>
            )}
          </div>

          {/* Export player list */}
          <div className="card" style={{ marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 10 }}>EXPORT PLAYER LIST</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 10 }}>
              Select a contest in the Overrides tab, then export a CSV with entry name, display name, email and points.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button onClick={exportEmails} disabled={exporting} className="btn btn-sm" style={{ background: "#00C853", color: "#0a1628", fontWeight: 700 }}>
                {exporting ? "Exporting..." : "⬇️ Export CSV"}
              </button>
              {exportStatus && (
                <span style={{ fontSize: 13, color: exportStatus.startsWith("✓") ? "#00C853" : "#E53935" }}>
                  {exportStatus}
                </span>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: "rgba(255,255,255,0.2)", lineHeight: 1.6 }}>
            All syncing is fully automated. Odds sync every 2 hours. Scores poll every 5 minutes during game windows
            (Thu/Sun/Mon nights + Sat late-season). Closing lines lock automatically at kickoff using historical odds.
            Auto-assignment runs automatically after the first kickoff of each week.
          </div>
        </div>
      )}

      {/* ── Overrides Tab ── */}
      {tab === "overrides" && (
        <div>
          <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(229,57,53,0.08)", border: "1px solid rgba(229,57,53,0.2)", borderRadius: 8, fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
            ⚠️ Manual overrides for edge cases only — incorrect ESPN scores, disputed games, API failures.
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, display: "block", marginBottom: 6 }}>CONTEST</label>
              <select
                value={selectedContest}
                onChange={(e) => setSelectedContest(e.target.value)}
                style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "9px 12px", fontSize: 13 }}
              >
                <option value="">Select contest...</option>
                {contests.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 90 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", letterSpacing: 1, display: "block", marginBottom: 6 }}>WEEK</label>
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(Number(e.target.value))}
                style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)", color: "white", borderRadius: 8, padding: "9px 12px", fontSize: 13 }}
              >
                {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedContest ? (
            <OverridePanel contestId={selectedContest} week={selectedWeek} />
          ) : (
            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>Select a contest to see override options.</p>
          )}
        </div>
      )}
    </div>
  );
}
